import React from "react";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { musicalGenreDisplayId } from "../../../musicalGenre/store/MusicalGenreCommonStore";
import { ShowItemsCompact } from "../../../common/controls";
import { GroupContainer } from "../../../../common/components";
import { ShowReferenceSet } from "../../../common/controls";
import { interpreterDisplayId } from "../../../interpreter/store/InterpreterCommonStore";
import { territoryDisplayId } from "../../../territory/store/TerritoryCommonStore";
import { useSnapshotModalStack } from "../../../common/controls";
import { EditionView as SnapshotEditionView } from "./EditionView";
import { EditionTypeView as SnapshotEditionTypeView } from "./EditionTypeView";
import { PhonogramView as SnapshotPhonogramView } from "./PhonogramView";
import { MusicalWorkLinkView as SnapshotMusicalWorkLinkView } from "./MusicalWorkLinkView";
import { PlatformView as SnapshotPlatformView } from "./PlatformView";
import { PlatformLinkTypeView as SnapshotPlatformLinkTypeView } from "./PlatformLinkTypeView";
import { MusicalGenreView as SnapshotMusicalGenreView } from "./MusicalGenreView";
import { InterpreterView as SnapshotInterpreterView } from "./InterpreterView";
import { TerritoryView as SnapshotTerritoryView } from "./TerritoryView";
import { WorkAuthorView as SnapshotWorkAuthorView } from "./WorkAuthorView";
import { AuthorComposerView as SnapshotAuthorComposerView } from "./AuthorComposerView";
import { CountryView as SnapshotCountryView } from "./CountryView";
import { ProvinceStateView as SnapshotProvinceStateView } from "./ProvinceStateView";
import { AuthorCollectingSocietyView as SnapshotAuthorCollectingSocietyView } from "./AuthorCollectingSocietyView";
import { CollectiveManagementSocietyView as SnapshotCollectiveManagementSocietyView } from "./CollectiveManagementSocietyView";
import { WorkAuthorPublisherView as SnapshotWorkAuthorPublisherView } from "./WorkAuthorPublisherView";
import { PublisherView as SnapshotPublisherView } from "./PublisherView";
import { PublisherRepresentativeView as SnapshotPublisherRepresentativeView } from "./PublisherRepresentativeView";
import { PublisherCollectingSocietyView as SnapshotPublisherCollectingSocietyView } from "./PublisherCollectingSocietyView";
import { EditionDocumentationView as SnapshotEditionDocumentationView } from "./EditionDocumentationView";
import { EditionContractPublisherView as SnapshotEditionContractPublisherView } from "./EditionContractPublisherView";
import { AssociatedDocumentEditionView as SnapshotAssociatedDocumentEditionView } from "./AssociatedDocumentEditionView";
import { EditionDocumentTypeView as SnapshotEditionDocumentTypeView } from "./EditionDocumentTypeView";
import { UserAttachmentEditionView as SnapshotUserAttachmentEditionView } from "./UserAttachmentEditionView";
import { useFileCellAffordances } from "../../../common/controls";
import {
  useSnapshotExtension,
  useSnapshotExtensionTop,
} from "../../../common/controls";
import { Box, FormControl, Typography } from "@mui/material";

type MusicalWorkViewProps = {
  snapshotData: any;
  // Propagated from the top-level snapshot view so nested tables can resolve
  // the snapshot-scoped file-download endpoint (/<snapshotEntityName>.data/
  // <snapshotRecordId>/<fileUuid>). Both are optional at the type level but
  // required at runtime for the download / view affordances to work.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export const MusicalWorkView: React.FC<MusicalWorkViewProps> = ({
  snapshotData,
  snapshotEntityName,
  snapshotRecordId,
}) => {
  const { langStore } = useRootStore();
  const renderExtensionTop = useSnapshotExtensionTop("MusicalWork");
  const renderExtension = useSnapshotExtension("MusicalWork");

  const { pushModal, modalElement } = useSnapshotModalStack(
    (entityName, data) => {
      switch (entityName) {
        case "Edition":
          return (
            <SnapshotEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionType":
          return (
            <SnapshotEditionTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Phonogram":
          return (
            <SnapshotPhonogramView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWorkLink":
          return (
            <SnapshotMusicalWorkLinkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Platform":
          return (
            <SnapshotPlatformView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PlatformLinkType":
          return (
            <SnapshotPlatformLinkTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalGenre":
          return (
            <SnapshotMusicalGenreView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Interpreter":
          return (
            <SnapshotInterpreterView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Territory":
          return (
            <SnapshotTerritoryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "WorkAuthor":
          return (
            <SnapshotWorkAuthorView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorComposer":
          return (
            <SnapshotAuthorComposerView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Country":
          return (
            <SnapshotCountryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "ProvinceState":
          return (
            <SnapshotProvinceStateView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorCollectingSociety":
          return (
            <SnapshotAuthorCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "CollectiveManagementSociety":
          return (
            <SnapshotCollectiveManagementSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "WorkAuthorPublisher":
          return (
            <SnapshotWorkAuthorPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Publisher":
          return (
            <SnapshotPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherRepresentative":
          return (
            <SnapshotPublisherRepresentativeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherCollectingSociety":
          return (
            <SnapshotPublisherCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentation":
          return (
            <SnapshotEditionDocumentationView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionContractPublisher":
          return (
            <SnapshotEditionContractPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AssociatedDocumentEdition":
          return (
            <SnapshotAssociatedDocumentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentType":
          return (
            <SnapshotEditionDocumentTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "UserAttachmentEdition":
          return (
            <SnapshotUserAttachmentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        default:
          return null;
      }
    }
  );
  const { renderFileCell, excelViewerElement } = useFileCellAffordances({
    snapshotEntityName,
    snapshotRecordId,
    enableDownload: true,
  });
  return (
    <>
      {renderExtensionTop ? renderExtensionTop(snapshotData) : null}
      <GroupContainer
        id="workInfo"
        title={langStore.groupMusicalWorkWorkInfoLabel}
      >
        <FormControl fullWidth>
          <ShowMultiLine
            label={langStore.MusicalWorkTitleLabel}
            value={String(snapshotData?.title ?? "")}
          />
          <ShowReference
            label={langStore.MusicalWorkMusicalGenreLabel}
            selectedValue={
              snapshotData?.musicalGenre
                ? langStore.displayIdByLang(
                    musicalGenreDisplayId(snapshotData?.musicalGenre, langStore)
                  )
                : ""
            }
            onView={() => pushModal("MusicalGenre", snapshotData?.musicalGenre)}
          />
          <ShowItemsCompact
            label={langStore.MusicalWorkAuthorsLabel}
            showLabel={true}
            enableDownload={true}
            entityName="WorkAuthor"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              {
                name: "lyricsPercentage",
                title: langStore.WorkAuthorLyricsPercentageLabel,
              },
              {
                name: "musicPercentage",
                title: langStore.WorkAuthorMusicPercentageLabel,
              },
            ]}
            rows={(snapshotData?.authors ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                lyricsPercentage: __r?.lyricsPercentage ?? "",
                musicPercentage: __r?.musicPercentage ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal("WorkAuthor", (snapshotData?.authors ?? [])[idx]);
            }}
          />
          <ShowMultiLine
            label={langStore.MusicalWorkNotesLabel}
            value={String(snapshotData?.notes ?? "")}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer
        id="workRegistration"
        title={langStore.groupMusicalWorkWorkRegistrationLabel}
      >
        <FormControl fullWidth>
          <ShowReferenceSet
            label={langStore.MusicalWorkPerformersLabel}
            selectedValues={(snapshotData?.performers ?? []).map(
              (__x: any, __i: number) => ({
                id: String(__i),
                displayId: interpreterDisplayId(__x, langStore),
              })
            )}
            onView={(id) => {
              const idx = parseInt(id, 10);
              pushModal("Interpreter", (snapshotData?.performers ?? [])[idx]);
            }}
          />
          <ShowMultiLine
            label={langStore.MusicalWorkCmsIdLabel}
            value={String(snapshotData?.cmsId ?? "")}
          />
          <ShowReferenceSet
            label={langStore.MusicalWorkTerritoriesLabel}
            selectedValues={(snapshotData?.territories ?? []).map(
              (__x: any, __i: number) => ({
                id: String(__i),
                displayId: territoryDisplayId(__x, langStore),
              })
            )}
            onView={(id) => {
              const idx = parseInt(id, 10);
              pushModal("Territory", (snapshotData?.territories ?? [])[idx]);
            }}
          />
          <ShowMultiLine
            label={langStore.MusicalWorkIswcLabel}
            value={String(snapshotData?.iswc ?? "")}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer
        id="workContent"
        title={langStore.groupMusicalWorkWorkContentLabel}
      >
        <FormControl fullWidth>
          <Box sx={{ my: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {langStore.MusicalWorkScoreLabel}
            </Typography>
            {snapshotData?.score?.filename ? (
              <Box sx={{ display: "flex" }}>
                {renderFileCell(
                  {
                    score: snapshotData?.score.filename,
                    score_data: snapshotData?.score,
                  },
                  "score",
                  snapshotData?.score?.mimetype ?? "",
                  snapshotData?.score.filename
                )}
              </Box>
            ) : null}
          </Box>
          <ShowItemsCompact
            label={langStore.MusicalWorkPhonogramsLabel}
            showLabel={true}
            enableDownload={true}
            entityName="Phonogram"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              { name: "audioFile", title: langStore.PhonogramAudioFileLabel },
              { name: "trackName", title: langStore.PhonogramTrackNameLabel },
              { name: "isrc", title: langStore.PhonogramIsrcLabel },
              { name: "albumName", title: langStore.PhonogramAlbumNameLabel },
              {
                name: "publicationYear",
                title: langStore.PhonogramPublicationYearLabel,
              },
              { name: "mainAudio", title: langStore.PhonogramMainAudioLabel },
            ]}
            rows={(snapshotData?.phonograms ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                audioFile: __r?.audioFile?.filename ?? "",
                audioFile_data: __r?.audioFile,
                trackName: __r?.trackName ?? "",
                isrc: __r?.isrc ?? "",
                albumName: __r?.albumName ?? "",
                publicationYear: __r?.publicationYear ?? "",
                mainAudio: __r?.mainAudio ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal("Phonogram", (snapshotData?.phonograms ?? [])[idx]);
            }}
          />
          <ShowMultiLine
            label={langStore.MusicalWorkOriginalLyricsLabel}
            value={String(snapshotData?.originalLyrics ?? "")}
          />
        </FormControl>
      </GroupContainer>
      {renderExtension ? renderExtension(snapshotData) : null}

      {modalElement}
      {excelViewerElement}
    </>
  );
};
